import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Mail, TrendingUp, BarChart3, Target, RefreshCw, 
  Copy, Sparkles, FileText, Brain, ChevronDown, ChevronUp, Trash2,
  Building2, MapPin, HardHat, Download,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DevisForm from '@/components/forms/DevisForm';
import NewDevisDialog from '@/components/NewDevisDialog';
import { DevisAIActions } from '@/components/AIActionButtons';
import DevisCopier from '@/components/DevisCopier';
import DevisGenerator from '@/components/DevisGenerator';
import PriceSearchPanel from '@/components/PriceSearchPanel';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Devis() {
  const [devis, setDevis] = useState([]);
  const [filteredDevis, setFilteredDevis] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [contacts, setContacts] = useState({});
  const [showAITools, setShowAITools] = useState(false);
  const [activeAITool, setActiveAITool] = useState(null); // 'copier', 'generator', 'prix'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | Brouillon | Envoyé | Accepté | Refusé
  const [ceeFilter, setCeeFilter] = useState('classic'); // 'classic' | 'cee' — défaut Classique
  const navigate = useNavigate();

  // Resolve client info: prefer client_info embedded in devis, fallback to contacts map
  const getClient = (d) => {
    const ci = d.client_info || {};
    const c = contacts[d.contact_id] || {};
    return {
      nom: ci.nom || c.nom || 'Client non renseigné',
      ville: ci.ville || c.ville || '',
      code_postal: ci.code_postal || c.code_postal || '',
      adresse: ci.adresse || c.adresse || '',
    };
  };

  useEffect(() => {
    document.title = 'Devis - Reno Resolve';
    fetchDevis();
    fetchContacts();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    const filtered = devis.filter((d) => {
      const cl = getClient(d);
      const matchesSearch = !q ||
        d.devis_id?.toLowerCase().includes(q) ||
        d.type_prestation?.toLowerCase().includes(q) ||
        d.project_title?.toLowerCase().includes(q) ||
        cl.nom.toLowerCase().includes(q) ||
        cl.ville.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all'
        ? true
        : statusFilter === 'won'
          ? ['Accepté', 'Signé', 'Facturé'].includes(d.statut)
          : d.statut === statusFilter;
      const matchesCee = ceeFilter === 'cee'
        ? Boolean(d.cee_enabled || d.cee_dossier_id)
        : !d.cee_enabled && !d.cee_dossier_id;
      return matchesSearch && matchesStatus && matchesCee;
    });
    setFilteredDevis(filtered);
  }, [search, devis, contacts, statusFilter, ceeFilter]);

  const fetchDevis = async () => {
    try {
      const [dRes, chRes] = await Promise.all([
        api.get('/devis'),
        api.get('/chantiers').catch(() => ({ data: [] })),
      ]);
      // Map devis_id → chantier_id
      const chMap = {};
      (chRes.data || []).forEach((c) => {
        if (c.devis_id) chMap[c.devis_id] = c.chantier_id;
      });
      const enriched = (dRes.data || []).map((d) => ({ ...d, _chantier_id: chMap[d.devis_id] || null }));
      setDevis(enriched);
      setFilteredDevis(enriched);
      try {
        sessionStorage.setItem('devis_list_order',
          JSON.stringify(enriched.map(d => d.devis_id)));
      } catch (_) {}
    } catch (error) {
      console.error('Erreur chargement devis:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      const contactsMap = {};
      response.data.forEach(c => { contactsMap[c.contact_id] = c; });
      setContacts(contactsMap);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
  };

  const handleDevisCreated = () => {
    setShowForm(false);
    setActiveAITool(null);
    fetchDevis();
    toast.success('Devis créé avec succès');
  };

  const handleDeleteDevis = async (devisId, e) => {
    e.stopPropagation();
    if (!window.confirm('Voulez-vous vraiment supprimer ce devis ?')) return;
    try {
      await api.delete(`/devis/${devisId}`);
      toast.success('Devis supprimé');
      fetchDevis();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicateDevis = async (devisId, e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/devis/${devisId}/duplicate`);
      toast.success(`Devis dupliqué : ${res.data.devis_id}`);
      fetchDevis();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleChangeStatus = async (devisId, currentStatus, newStatus) => {
    if (newStatus === currentStatus) return;
    // Optimistic update
    setDevis((prev) => prev.map((d) => (d.devis_id === devisId ? { ...d, statut: newStatus } : d)));
    try {
      await api.patch(`/devis/${devisId}/status`, { statut: newStatus });
      toast.success(`Statut → ${newStatus}`);
      // Background refetch (chantier auto-creation, lifecycle dates, etc.)
      fetchDevis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
      // Roll back
      setDevis((prev) => prev.map((d) => (d.devis_id === devisId ? { ...d, statut: currentStatus } : d)));
    }
  };

  const getStatutBadgeVariant = (statut) => {
    const variants = {
      Brouillon: 'secondary',
      'Envoyé': 'default',
      'Relancé': 'secondary',
      'Accepté': 'success',
      'Signé': 'success',
      'Facturé': 'success',
      'Refusé': 'destructive',
    };
    return variants[statut] || 'default';
  };

  // Status filter chips counts
  const statusCounts = useMemo(() => {
    const c = {
      all: devis.length,
      Brouillon: 0,
      'Envoyé': 0,
      'Accepté': 0,
      'Facturé': 0,
      'Refusé': 0,
      won: 0,
    };
    devis.forEach((d) => {
      if (c[d.statut] !== undefined) c[d.statut] += 1;
      if (['Accepté', 'Signé', 'Facturé'].includes(d.statut)) c.won += 1;
    });
    return c;
  }, [devis]);

  // Color a client avatar based on the name (deterministic)
  const avatarStyle = (name) => {
    const palette = [
      ['bg-blue-100', 'text-blue-700'],
      ['bg-emerald-100', 'text-emerald-700'],
      ['bg-purple-100', 'text-purple-700'],
      ['bg-amber-100', 'text-amber-700'],
      ['bg-rose-100', 'text-rose-700'],
      ['bg-indigo-100', 'text-indigo-700'],
      ['bg-teal-100', 'text-teal-700'],
      ['bg-orange-100', 'text-orange-700'],
    ];
    let h = 0;
    for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  };

  const initials = (name) => {
    if (!name) return '?';
    const parts = name.replace(/Mairie de |Commune de |Ville de /gi, '').trim().split(/\s+/);
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  };

  const getSourceBadge = (source) => {
    if (source === 'copie_concurrent') {
      return <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">Copie</Badge>;
    }
    if (source === 'generateur_ia') {
      return <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">IA</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Devis
          </h1>
          <p className="text-slate-600 mt-1">
            {filteredDevis.length === devis.length
              ? `${devis.length} devis total`
              : `${filteredDevis.length} sur ${devis.length} devis`}
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowAITools(!showAITools)}
            className={showAITools ? 'bg-purple-50 border-purple-200' : ''}
            data-testid="toggle-ai-tools-btn"
          >
            <Brain className="w-4 h-4 mr-2 text-purple-600" />
            Outils IA
            {showAITools ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="create-devis-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Devis
          </Button>
        </div>
      </div>

      {/* AI Tools Section */}
      {showAITools && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Outils IA pour vos devis
          </h2>
          
          {!activeAITool ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Copy competitor quote */}
              <button
                onClick={() => setActiveAITool('copier')}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all text-left group"
                data-testid="ai-tool-copier"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <Copy className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Copier un devis concurrent</h3>
                    <p className="text-sm text-purple-600">Analysez et sous-cotez</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Uploadez le PDF ou photo d'un devis concurrent. L'IA extrait les infos, vous ajustez les prix et générez votre propre devis.
                </p>
              </button>
              
              {/* Generate from description */}
              <button
                onClick={() => setActiveAITool('generator')}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all text-left group"
                data-testid="ai-tool-generator"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Générer un devis IA</h3>
                    <p className="text-sm text-blue-600">Texte ou vocal</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Décrivez votre chantier à l'oral ou par écrit. L'IA génère automatiquement les lignes de devis avec les prix du marché.
                </p>
              </button>

              {/* Price search */}
              <button
                onClick={() => setActiveAITool('prix')}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all text-left group"
                data-testid="ai-tool-prix"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                    <Search className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Prix fournisseurs</h3>
                    <p className="text-sm text-violet-600">Point P, Leroy Merlin, etc.</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Recherchez les prix actuels de matériaux chez Point P, Leroy Merlin, Castorama, Plateforme du Bâtiment et sauvegardez-les.
                </p>
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {activeAITool === 'copier' ? (
                    <>
                      <Copy className="w-5 h-5 text-purple-600" />
                      <span className="font-medium">Copier un devis concurrent</span>
                    </>
                  ) : activeAITool === 'prix' ? (
                    <>
                      <Search className="w-5 h-5 text-violet-600" />
                      <span className="font-medium">Recherche prix fournisseurs</span>
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Générateur de devis IA</span>
                    </>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setActiveAITool(null)}>
                  Retour aux options
                </Button>
              </div>
              
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                {activeAITool === 'copier' ? (
                  <DevisCopier onDevisCreated={handleDevisCreated} />
                ) : activeAITool === 'prix' ? (
                  <PriceSearchPanel />
                ) : (
                  <DevisGenerator onDevisCreated={handleDevisCreated} />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{devis.length}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{devis.filter(d => d.statut === 'Envoyé').length}</p>
              <p className="text-xs text-slate-500">En attente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{devis.filter(d => ['Accepté', 'Signé', 'Facturé'].includes(d.statut)).length}</p>
              <p className="text-xs text-slate-500">Gagnés</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">
                {devis.length > 0 ? Math.round(devis.filter(d => ['Accepté', 'Signé', 'Facturé'].includes(d.statut)).length / devis.length * 100) : 0}%
              </p>
              <p className="text-xs text-slate-500">Conversion</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search + status filter chips */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher par client, ville, ID devis, type ou projet..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-devis-input"
            />
          </div>
          <Button variant="outline" onClick={fetchDevis} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {/* Filtre Type : Classique / CEE — défaut Classique */}
        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</span>
          <button
            onClick={() => setCeeFilter('classic')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              ceeFilter === 'classic'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
            data-testid="devis-filter-classic"
          >
            Classiques
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              ceeFilter === 'classic' ? 'bg-white/20' : 'bg-white/70'
            }`}>
              {devis.filter((d) => !d.cee_enabled && !d.cee_dossier_id).length}
            </span>
          </button>
          <button
            onClick={() => setCeeFilter('cee')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              ceeFilter === 'cee'
                ? 'bg-blue-700 text-white'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
            data-testid="devis-filter-cee"
          >
            CEE
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              ceeFilter === 'cee' ? 'bg-white/20' : 'bg-white/70'
            }`}>
              {devis.filter((d) => d.cee_enabled || d.cee_dossier_id).length}
            </span>
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all',       label: 'Tous',        cls: 'bg-slate-100 text-slate-700 hover:bg-slate-200',     active: 'bg-slate-900 text-white' },
            { key: 'Brouillon', label: 'Brouillon',   cls: 'bg-slate-50 text-slate-600 hover:bg-slate-100',      active: 'bg-slate-700 text-white' },
            { key: 'Envoyé',    label: 'Envoyés',     cls: 'bg-blue-50 text-blue-700 hover:bg-blue-100',         active: 'bg-blue-600 text-white' },
            { key: 'won',       label: 'Gagnés',      cls: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', active: 'bg-emerald-600 text-white' },
            { key: 'Facturé',   label: 'Facturés',    cls: 'bg-teal-50 text-teal-700 hover:bg-teal-100',         active: 'bg-teal-600 text-white' },
            { key: 'Refusé',    label: 'Refusés',     cls: 'bg-red-50 text-red-700 hover:bg-red-100',            active: 'bg-red-600 text-white' },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s.key ? s.active : s.cls
              }`}
              data-testid={`status-chip-${s.key}`}
            >
              {s.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                statusFilter === s.key ? 'bg-white/20' : 'bg-white/70'
              }`}>
                {statusCounts[s.key] || 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredDevis.length === 0 ? (
          <div className="bg-white rounded-lg p-6 text-center text-slate-500 border border-slate-200">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">Aucun devis trouvé</p>
          </div>
        ) : (
          filteredDevis.map((d) => {
            const cl = getClient(d);
            const [bg, txt] = avatarStyle(cl.nom);
            return (
              <div
                key={`m-${d.devis_id}`}
                className="bg-white rounded-lg p-3 border border-slate-200 active:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/devis/${d.devis_id}`)}
                data-testid={`devis-card-${d.devis_id}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-full ${bg} ${txt} flex items-center justify-center text-xs font-bold shrink-0`}>
                    {(cl.nom || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{cl.nom || 'Client inconnu'}</p>
                    <p className="text-[10px] font-mono text-slate-500 truncate">{d.devis_id}</p>
                  </div>
                  <Badge variant={getStatutBadgeVariant(d.statut)} className="text-[10px] shrink-0">{d.statut}</Badge>
                </div>
                {d.project_title && (
                  <p className="text-xs text-slate-600 line-clamp-2 mb-2">{d.project_title}</p>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-blue-600">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(d.totaux?.total_ttc || d.montant_estime || 0)}
                  </span>
                  <span className="text-slate-500">
                    {d.date_creation ? format(new Date(d.date_creation), 'dd/MM/yy', { locale: fr }) : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100 justify-end">
                  {d._chantier_id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/chantiers/${d._chantier_id}`); }}
                      className="p-1.5 rounded text-blue-600 hover:bg-blue-50"
                      title="Voir chantier"
                    >
                      <HardHat className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDuplicateDevis(d.devis_id, e)}
                    className="p-1.5 rounded text-blue-500 hover:bg-blue-50"
                    title="Dupliquer"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteDevis(d.devis_id, e)}
                    className="p-1.5 rounded text-red-500 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop Table (div-grid pour éviter <span> injectés dans <tbody>) */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="w-full" role="table">
            {/* Header */}
            <div role="row" className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1.4fr] bg-slate-50 border-b border-slate-200">
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Client</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Type / Projet</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Montant</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Statut</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</div>
            </div>

            {/* Body */}
            <div role="rowgroup" className="divide-y divide-slate-200">
              {filteredDevis.length === 0 ? (
                <div role="row" className="px-6 py-12 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun devis trouvé</p>
                  <p className="text-sm mt-2">
                    {search || statusFilter !== 'all' ? 'Essayez de modifier les filtres ci-dessus' : 'Créez votre premier devis avec les outils IA ci-dessus !'}
                  </p>
                </div>
              ) : (
                filteredDevis.map((d) => {
                  const cl = getClient(d);
                  const [bg, txt] = avatarStyle(cl.nom);
                  return (
                    <div
                      key={d.devis_id}
                      role="row"
                      className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1.4fr] items-center hover:bg-slate-50 transition-colors"
                      data-testid={`devis-row-${d.devis_id}`}
                    >
                      {/* Client column with avatar + name + ID */}
                      <div role="cell" className="px-6 py-4 cursor-pointer min-w-0" onClick={() => navigate(`/devis/${d.devis_id}`)}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center font-semibold text-sm shrink-0 ${txt}`}>
                            {initials(cl.nom)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate" title={cl.nom}>{cl.nom}</p>
                            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                              <span className="font-mono">{d.devis_id}</span>
                              {getSourceBadge(d.source) && (
                                <span className="shrink-0">{getSourceBadge(d.source)}</span>
                              )}
                              {(cl.ville || cl.code_postal) && (
                                <span className="inline-flex items-center gap-0.5 truncate">
                                  <MapPin className="w-3 h-3" />
                                  {[cl.code_postal, cl.ville].filter(Boolean).join(' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Type / Projet */}
                      <div role="cell" className="px-6 py-4 cursor-pointer min-w-0" onClick={() => navigate(`/devis/${d.devis_id}`)}>
                        <Badge>{d.type_prestation || 'BTP'}</Badge>
                        {d.project_title && (
                          <p className="text-xs text-slate-500 mt-1 truncate" title={d.project_title}>{d.project_title}</p>
                        )}
                      </div>

                      {/* Montant */}
                      <div role="cell" className="px-6 py-4 font-semibold text-slate-900 cursor-pointer" onClick={() => navigate(`/devis/${d.devis_id}`)}>
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(d.montant_estime || d.montant_total || 0)}
                      </div>

                      {/* Statut — cliquable pour changer */}
                      <div role="cell" className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:ring-2 hover:ring-blue-200 transition-all focus:outline-none"
                              data-testid={`status-trigger-${d.devis_id}`}
                              title="Cliquez pour changer le statut"
                            >
                              <Badge variant={getStatutBadgeVariant(d.statut)}>{d.statut}</Badge>
                              <ChevronDown className="w-3 h-3 text-slate-400" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-44">
                            {['Brouillon', 'Envoyé', 'Relancé', 'Accepté', 'Facturé', 'Refusé'].map((s) => (
                              <DropdownMenuItem
                                key={s}
                                onSelect={() => handleChangeStatus(d.devis_id, d.statut, s)}
                                disabled={s === d.statut}
                                className={`cursor-pointer ${s === d.statut ? 'opacity-50' : ''}`}
                                data-testid={`status-option-${d.devis_id}-${s}`}
                              >
                                <Badge variant={getStatutBadgeVariant(s)} className="mr-2">{s}</Badge>
                                {s === d.statut && <span className="text-xs text-slate-400 ml-auto">actuel</span>}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Date */}
                      <div role="cell" className="px-6 py-4 text-sm text-slate-600 cursor-pointer" onClick={() => navigate(`/devis/${d.devis_id}`)}>
                        {d.date_creation ? format(new Date(d.date_creation), 'dd/MM/yyyy', { locale: fr }) : '-'}
                      </div>

                      {/* Actions */}
                      <div role="cell" className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {d._chantier_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/chantiers/${d._chantier_id}`); }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`open-chantier-${d.devis_id}`}
                              title="Voir le chantier lié"
                            >
                              <HardHat className="w-4 h-4" />
                            </Button>
                          )}
                          {d.facture_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/factures/${d.facture_id}`); }}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`open-facture-${d.devis_id}`}
                              title="Voir la facture liée"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await api.get(`/devis/${d.devis_id}/pdf`, { responseType: 'blob' });
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Devis_${d.devis_id}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                toast.error('Erreur téléchargement PDF');
                              }
                            }}
                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                            data-testid={`download-devis-${d.devis_id}`}
                            title="Télécharger PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDuplicateDevis(d.devis_id, e)}
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`duplicate-devis-${d.devis_id}`}
                            title="Dupliquer"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteDevis(d.devis_id, e)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-devis-${d.devis_id}`}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New devis chooser — IA Copier / IA Libre / Création manuelle */}
      <NewDevisDialog
        open={showForm}
        onOpenChange={setShowForm}
        onDevisCreated={handleDevisCreated}
      />
    </div>
  );
}
